/**
 * Attaches speaker labels to timed transcript segments.
 *
 * The segments file carries timings but no speakers; `original_transcript`
 * carries speakers but no timings. In practice the diarized transcript is the
 * segment texts grouped into speaker turns and joined with spaces, so the two
 * can be walked in parallel to recover both.
 *
 * Timings are always taken verbatim from the segments. A matching failure can
 * only cost a speaker label, never playback accuracy.
 */

// Matches a turn prefix at the start of a line: "SPEAKER 1:", "Speaker 0:",
// "Doctor:", "Speaker:". Requiring a capitalized label of at most two words
// keeps a wrapped mid-sentence colon ("rest and ice: twice a day") from being
// read as a new turn.
const SPEAKER_LINE =
  /^[ \t]*([A-Z][A-Za-z]{0,19}(?:[ \t]+[A-Za-z0-9]{1,19})?)[ \t]*:[ \t]*(.*)$/;

/** How many upcoming turns to search when the walk loses its place. */
const RESYNC_LOOKAHEAD_TURNS = 4;

/** Words compared for alignment only — never rendered. */
const toTokens = (text) =>
  String(text ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9\s']/g, " ")
    .split(/\s+/)
    .filter(Boolean);

/**
 * Split a diarized transcript into speaker turns.
 *
 * Consecutive lines without their own prefix are treated as continuations of
 * the current turn, so hard-wrapped transcripts stay in one turn.
 *
 * @param {string} originalTranscript
 * @returns {{speaker: string, text: string}[]} empty when nothing is labelled
 */
export const parseSpeakerTurns = (originalTranscript) => {
  const lines = String(originalTranscript ?? "").split(/\r?\n/);
  const turns = [];

  for (const line of lines) {
    if (!line.trim()) continue;
    const match = SPEAKER_LINE.exec(line);

    if (match) {
      const speaker = match[1].replace(/\s+/g, " ").trim();
      turns.push({ speaker, text: match[2].trim() });
      continue;
    }

    // Continuation of the turn already in progress. Text before any label at
    // all is dropped: without a speaker it cannot be attributed.
    if (turns.length > 0) {
      const current = turns[turns.length - 1];
      current.text = `${current.text} ${line.trim()}`.trim();
    }
  }

  return turns.filter((turn) => turn.text);
};

/**
 * Tag each segment with the speaker whose turn it falls in.
 *
 * Walks segments and turn tokens together. On a clean match the cursor advances
 * by the segment's token count. On a mismatch it searches the next few turns for
 * the segment's opening tokens and jumps there; failing that it keeps the
 * current speaker and advances anyway, so one bad segment cannot cascade.
 *
 * @param {{start: number, end?: number, text: string}[]} segments
 * @param {{speaker: string, text: string}[]} turns
 * @returns {({speaker: string|null} & object)[]} segments in input order
 */
export const assignSpeakers = (segments, turns) => {
  const list = Array.isArray(segments) ? segments : [];
  const turnList = Array.isArray(turns) ? turns : [];
  if (list.length === 0) return [];
  if (turnList.length === 0) {
    return list.map((segment) => ({ ...segment, speaker: null }));
  }

  const turnTokens = turnList.map((turn) => toTokens(turn.text));

  let turnIndex = 0;
  let tokenIndex = 0;

  const startsTurnAt = (index, tokens) => {
    if (tokens.length === 0) return false;
    const candidate = turnTokens[index];
    if (!candidate) return false;
    return candidate[0] === tokens[0];
  };

  return list.map((segment) => {
    const tokens = toTokens(segment.text);

    // Consume finished turns so the cursor sits on one with tokens remaining.
    while (
      turnIndex < turnList.length &&
      tokenIndex >= turnTokens[turnIndex].length
    ) {
      turnIndex += 1;
      tokenIndex = 0;
    }

    if (turnIndex >= turnList.length) {
      // Segments past the end of the transcript inherit the last known speaker.
      const last = turnList[turnList.length - 1];
      return { ...segment, speaker: last.speaker };
    }

    const expected = turnTokens[turnIndex];
    const matchesHere = tokens.length > 0 && expected[tokenIndex] === tokens[0];

    if (!matchesHere) {
      // Lost the place. Prefer a turn boundary that starts with this segment.
      let resyncedTo = -1;
      const limit = Math.min(
        turnList.length,
        turnIndex + 1 + RESYNC_LOOKAHEAD_TURNS
      );
      for (let candidate = turnIndex + 1; candidate < limit; candidate += 1) {
        if (startsTurnAt(candidate, tokens)) {
          resyncedTo = candidate;
          break;
        }
      }
      if (resyncedTo !== -1) {
        turnIndex = resyncedTo;
        tokenIndex = 0;
      }
    }

    const speaker = turnList[turnIndex].speaker;
    tokenIndex += tokens.length;
    return { ...segment, speaker };
  });
};

/**
 * Collapse consecutive same-speaker segments into turn blocks for rendering.
 *
 * @param {({speaker: string|null} & object)[]} segments
 * @returns {{speaker: string|null, segments: object[]}[]}
 */
export const groupBySpeaker = (segments) => {
  const list = Array.isArray(segments) ? segments : [];
  const groups = [];

  for (const segment of list) {
    const previous = groups[groups.length - 1];
    if (previous && previous.speaker === segment.speaker) {
      previous.segments.push(segment);
      continue;
    }
    groups.push({ speaker: segment.speaker ?? null, segments: [segment] });
  }

  return groups;
};

/**
 * Convenience wrapper: segments plus a diarized transcript to speaker blocks.
 *
 * @param {{start: number, end?: number, text: string}[]} segments
 * @param {string} originalTranscript
 */
export const buildSpeakerBlocks = (segments, originalTranscript) =>
  groupBySpeaker(assignSpeakers(segments, parseSpeakerTurns(originalTranscript)));

/**
 * Index of the segment playing at `time`, or -1 before the first one.
 *
 * Binary search on `start`, since this runs on every `timeupdate`. When `time`
 * lands in a gap between segments the preceding index is returned so the
 * highlight holds steady instead of flickering off.
 *
 * @param {{start: number, end?: number}[]} segments ascending by start
 * @param {number} time
 */
export const findActiveSegmentIndex = (segments, time) => {
  const list = Array.isArray(segments) ? segments : [];
  if (list.length === 0 || !Number.isFinite(time)) return -1;

  let low = 0;
  let high = list.length - 1;
  let found = -1;

  while (low <= high) {
    const mid = (low + high) >> 1;
    if (list[mid].start <= time) {
      found = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  return found;
};
